import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, Plus, Trash2, Search, FileText, Download } from 'lucide-react';
import DateInput from '@/components/ui/date-input';
import { formatDate, formatDateTime } from '@/lib/utils';

const inputCls = 'w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white';

export default function DokumenteTab({ kind = 'dokumente' }) {
  const isFiles = kind === 'dateien';
  const noun = isFiles ? 'Dateien' : 'Dokumente';
  const appHint = isFiles
    ? 'Erscheint in der App unter Dateien — nur Lehrer.'
    : 'Erscheint in der App unter Dokumente — alle Nutzer (Schüler, Eltern, Lehrer).';
  const tid = (name) => `${isFiles ? 'dateien' : 'dokumente'}-${name}`;
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [search, setSearch] = useState('');
  const [onlyOffline, setOnlyOffline] = useState(false);
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/app-verwaltung/documents', { params: { search: search || undefined } });
      setItems(r.data || []);
    } catch (e) { toast.error(`${noun} konnten nicht geladen werden`); }
  }, [search, noun]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get('/teachers', { params: { limit: 100 } }).then((r) => setTeachers(r.data || [])).catch(() => {}); }, []);

  const inArea = items.filter((n) =>
    isFiles ? (n.audience && n.audience !== 'public') : (!n.audience || n.audience === 'public'));
  const visible = inArea.filter((n) =>
    (!onlyOffline || n.status === 'offline') && (!onlyOnline || n.status === 'online'));

  const startNew = () => {
    setSel(null); setFile(null);
    setForm({
      title: '', description: '',
      audience: isFiles ? 'personal' : 'public',
      selected: [], visible_from: '', visible_until: '', push_enabled: true,
    });
    setEditing(true);
  };

  const save = async () => {
    if (!form.title?.trim()) { toast.error('Titel fehlt'); return; }
    const audience = isFiles
      ? (form.audience === 'selected' ? 'selected' : 'personal')
      : 'public';
    setBusy(true);
    try {
      if (sel) {
        await api.put(`/admin/app-verwaltung/documents/${sel.id}`, {
          title: form.title, description: form.description, audience,
          selected_teacher_ids: audience === 'selected' ? form.selected : null,
          visible_from: form.visible_from || null, visible_until: form.visible_until || null,
          push_enabled: !!form.push_enabled,
        });
      } else {
        const fd = new FormData();
        fd.append('title', form.title);
        fd.append('description', form.description || '');
        fd.append('audience', audience);
        fd.append('selected_teacher_ids', (form.selected || []).join(','));
        fd.append('visible_from', form.visible_from || '');
        fd.append('visible_until', form.visible_until || '');
        fd.append('push_enabled', form.push_enabled ? 'true' : 'false');
        if (file) fd.append('file', file);
        await api.post('/admin/app-verwaltung/documents', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success('Gespeichert');
      setEditing(false); setSel(null);
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Speichern fehlgeschlagen');
    }
    finally { setBusy(false); }
  };

  const toggle = async () => {
    try {
      const r = await api.post(`/admin/app-verwaltung/documents/${sel.id}/${sel.status === 'online' ? 'unpublish' : 'publish'}`);
      setSel(r.data); load();
      toast.success(r.data.status === 'online' ? 'Freigegeben' : 'Freigabe entfernt');
    } catch (e) { toast.error('Aktion fehlgeschlagen'); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/app-verwaltung/documents/${sel.id}`); setSel(null); load(); toast.success('Gelöscht'); }
    catch (e) { toast.error('Löschen fehlgeschlagen'); }
  };

  const download = async () => {
    try {
      const r = await api.get(`/admin/app-verwaltung/documents/${sel.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = sel.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download fehlgeschlagen'); }
  };

  const fmt = (d) => (d ? formatDateTime(d) || '—' : '—');
  const fmtD = (d) => (d ? formatDate(d) : null);
  const sizeFmt = (b) => b ? (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`) : '';
  const audienceLabel = (d) => {
    if (!d.audience || d.audience === 'public') return 'App: Dokumente (alle Nutzer)';
    if (d.audience === 'personal') return 'App: Dateien (alle Lehrer)';
    return `App: Dateien (Ausgewählte, ${d.selected_teacher_ids?.length || 0})`;
  };

  return (
    <div className="h-full flex">
      <div className="w-96 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-3 space-y-2 border-b border-slate-100">
          <p className="text-xs text-slate-500" data-testid={tid('hint')}>{appHint}</p>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={onlyOffline} onChange={(e) => { setOnlyOffline(e.target.checked); setOnlyOnline(false); }} data-testid={tid('filter-offline')} /> Nur freizugebende anzeigen
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={onlyOnline} onChange={(e) => { setOnlyOnline(e.target.checked); setOnlyOffline(false); }} data-testid={tid('filter-online')} /> Nur freigegebene anzeigen
          </label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Schnellsuche" value={search} onChange={(e) => setSearch(e.target.value)} data-testid={tid('search')} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {visible.map((n) => (
            <button key={n.id} onClick={() => { setSel(n); setEditing(false); }}
                    data-testid={`${tid('item')}-${n.id}`}
                    className={`w-full text-left flex items-stretch border-b border-slate-100 ${sel?.id === n.id ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`}>
              <span className="w-1.5 shrink-0" style={{ background: n.status === 'online' ? '#15803d' : '#ef4444' }} />
              <span className="flex-1 min-w-0 px-3 py-2.5">
                <span className="flex justify-between text-xs text-slate-400"><span>{n.filename || 'ohne Datei'}</span><span>{fmt(n.created_at)}</span></span>
                <span className="block text-sm font-medium truncate">{n.title}</span>
                <span className={`block text-[11px] font-medium truncate ${isFiles ? 'text-brand-warning' : 'text-slate-500'}`}>
                  {isFiles
                    ? (n.audience === 'selected' ? `Dateien · Ausgewählte (${n.selected_teacher_ids?.length || 0})` : 'Dateien · alle Lehrer')
                    : 'Dokumente · alle Nutzer'}
                </span>
                <span className="block text-xs text-slate-400 truncate">{n.description}</span>
              </span>
            </button>
          ))}
          {!visible.length && <p className="p-6 text-center text-sm text-slate-400">Keine {noun}</p>}
        </div>
        <p className="p-2 text-[11px] text-slate-400 border-t border-slate-100">Gefunden: {visible.length} / Gesamt: {inArea.length}</p>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex justify-end mb-4 gap-2">
          {sel && !editing && (
            <Button variant="outline" onClick={() => { setForm({ title: sel.title, description: sel.description, audience: sel.audience, selected: sel.selected_teacher_ids || [], visible_from: sel.visible_from || '', visible_until: sel.visible_until || '' }); setEditing(true); }} data-testid={tid('edit-btn')}>Bearbeiten</Button>
          )}
          <Button onClick={startNew} className="bg-brand-red hover:bg-brand-red/90" data-testid={tid('new-btn')}><Plus className="w-4 h-4 mr-1" /> Neu</Button>
        </div>

        {editing ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3 max-w-2xl">
            <input className={inputCls} placeholder="Titel" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid={tid('form-title')} />
            <textarea className={inputCls} rows={5} placeholder="Beschreibung…" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid={tid('form-description')} />
            {!sel && (
              <div>
                <label className="text-xs text-slate-500">Datei (PDF/JPG/PNG/MP3, max. 25 MB)</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.mp3,audio/mpeg" onChange={(e) => setFile(e.target.files[0])}
                       className="block text-sm mt-1" data-testid={tid('form-file')} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Sichtbar ab</label>
                <DateInput  className={inputCls} value={form.visible_from} onChange={(e) => setForm((p) => ({ ...p, visible_from: e.target.value }))} data-testid={tid('form-from')} /></div>
              <div><label className="text-xs text-slate-500">Sichtbar bis</label>
                <DateInput  className={inputCls} value={form.visible_until} onChange={(e) => setForm((p) => ({ ...p, visible_until: e.target.value }))} data-testid={tid('form-until')} /></div>
            </div>
            {isFiles ? (
              <select className={inputCls} value={form.audience} onChange={(e) => setForm((p) => ({ ...p, audience: e.target.value }))} data-testid={tid('form-audience')}>
                <option value="personal">Dateien — alle Lehrer</option>
                <option value="selected">Dateien — ausgewählte Lehrer</option>
              </select>
            ) : (
              <p className="text-xs text-slate-500 rounded-md border border-slate-200 px-3 py-2 bg-slate-50" data-testid={tid('form-audience')}>
                Sichtbarkeit: App-Bereich Dokumente (alle Nutzer)
              </p>
            )}
            {form.audience === 'selected' && (
              <div className="border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto grid grid-cols-2 gap-1">
                {teachers.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.selected?.includes(t.id)}
                           onChange={(e) => setForm((p) => ({ ...p, selected: e.target.checked ? [...(p.selected || []), t.id] : p.selected.filter((x) => x !== t.id) }))} />
                    {t.last_name}, {t.first_name}
                  </label>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.push_enabled}
                     onChange={(e) => setForm((p) => ({ ...p, push_enabled: e.target.checked }))}
                     data-testid={tid('form-push')} />
              Push-Benachrichtigung bei Freigabe senden
            </label>
            <div className="flex gap-2 pt-2">
              <Button onClick={save} disabled={busy} className="bg-brand-red hover:bg-brand-red/90" data-testid={tid('form-save')}>Speichern</Button>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid={tid('form-cancel')}>Abbrechen</Button>
            </div>
          </div>
        ) : sel ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-2xl" data-testid={tid('detail')}>
            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">{audienceLabel(sel)}</p>
            <h2 className="text-lg font-bold mb-3">{sel.title}</h2>
            <p className="text-sm whitespace-pre-wrap text-slate-700 mb-4">{sel.description}</p>
            {sel.filename && (
              <button onClick={download} className="flex items-center gap-2 text-sm text-brand-red font-medium" data-testid={tid('download-btn')}>
                <FileText className="w-4 h-4" /> {sel.filename} ({sizeFmt(sel.size_bytes)}) <Download className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <p className="text-slate-400 text-sm mt-20 text-center">{isFiles ? 'Datei' : 'Dokument'} links auswählen oder „Neu“ klicken</p>
        )}
      </div>

      {sel && !editing && (
        <div className="w-64 shrink-0 border-l border-slate-200 bg-white p-4 space-y-4">
          <Button onClick={toggle} className="w-full bg-brand-red hover:bg-brand-red/90" data-testid={tid('toggle-btn')}>
            {sel.status === 'online' ? <><EyeOff className="w-4 h-4 mr-1" /> Freigabe entfernen</> : <><Eye className="w-4 h-4 mr-1" /> Freigeben</>}
          </Button>
          <div className="text-sm space-y-3">
            <div><p className="text-xs text-slate-400">Status</p>
              <p className={sel.status === 'online' ? 'text-brand-success font-semibold' : 'text-rose-600 font-semibold'}>{sel.status === 'online' ? 'Online' : 'Offline'}</p></div>
            <div><p className="text-xs text-slate-400">App-Bereich</p><p>{audienceLabel(sel)}</p></div>
            <div><p className="text-xs text-slate-400">Zeitraum</p><p>{fmtD(sel.visible_from) ? `Ab ${fmtD(sel.visible_from)}` : 'Sofort'}{fmtD(sel.visible_until) ? ` bis ${fmtD(sel.visible_until)}` : ''}</p></div>
            <div><p className="text-xs text-slate-400">Push</p>
              <p data-testid={tid('push-info')}>{sel.push_sent_at ? `Gesendet: ${fmt(sel.push_sent_at)}` : sel.push_enabled !== false ? 'Bei Freigabe' : 'Deaktiviert'}</p></div>
            <div><p className="text-xs text-slate-400">Erstellung</p><p>{fmt(sel.created_at)}</p></div>
            <div><p className="text-xs text-slate-400">Änderung</p><p>{fmt(sel.updated_at)}</p></div>
          </div>
          <button onClick={remove} className="text-rose-500 text-sm flex items-center gap-1" data-testid={tid('delete-btn')}><Trash2 className="w-4 h-4" /> Löschen</button>
        </div>
      )}
    </div>
  );
}
